/**
 * Integration hooks for Tabitto <-> Mekke cross-app features
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "../../infrastructure/api/client";
import {
  ApiError,
  type SaveTabittoSpotToMekkeDto,
  type SaveTabittoSpotToMekkeResponseDto,
} from "@tabipla/shared";

interface MekkeStatusResponse {
  savedSpotIds: string[];
  savedPlaceIds: string[];
}

/**
 * Check if spots are saved in Mekke
 */
export function useMekkeStatus(spotIds: string[]) {
  return useQuery({
    queryKey: ["mekke-status", spotIds],
    queryFn: async () => {
      if (spotIds.length === 0) {
        return { savedSpotIds: [], savedPlaceIds: [] };
      }
      const response = await apiClient.get<MekkeStatusResponse>(
        `/api/integration/mekke-status?spotIds=${spotIds.join(",")}`
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to check Mekke status");
      }
      return response.data ?? { savedSpotIds: [], savedPlaceIds: [] };
    },
    enabled: spotIds.length > 0,
    staleTime: 30000, // Cache for 30 seconds
  });
}

/**
 * Save a Tabitto spot to Mekke
 */
export function useSaveToMekke() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: SaveTabittoSpotToMekkeDto) => {
      const response = await apiClient.post<SaveTabittoSpotToMekkeResponseDto>(
        "/api/integration/tabitto-to-mekke",
        data
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to save to Mekke");
      }
      return response.data;
    },
    onSuccess: () => {
      // Invalidate Mekke status queries to update the saved indicator
      queryClient.invalidateQueries({ queryKey: ["mekke-status"] });
      queryClient.invalidateQueries({ queryKey: ["mekke-spots"] });
    },
  });
}
